/**
 * React Query hooks for the billing surface.
 *
 * Cache keys are scope-parameterised exactly like `useUsageSummary` —
 * `activeOrgId` from `useOrgStore` switches between personal and org
 * wallets when the user flips OrgSwitcher. Authoritative state stays on
 * the server; this module is a thin caching wrapper over `billingApi`.
 */

import { ApiError } from "@/lib/axios";
import type {
  AutoRechargeConfig,
  TopUpPackId,
  UsageRange,
} from "@models/billing";
import { useAuthStore } from "@stores/authStore";
import { useOrgStore } from "@stores/orgStore";
import { toast } from "@stores/toastStore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { billingApi } from "../api";

// ── Error copy ────────────────────────────────────────────────────────────
// A 503 from the checkout/portal routes means Stripe isn't configured on
// the server (StripeNotConfigured → 503), not a transient blip — so tell
// the user to contact support rather than inviting an endless retry loop.
function billingErrorMessage(err: unknown, action: string): string {
  if (err instanceof ApiError && err.status === 503) {
    return "Payments aren't available right now — please contact support.";
  }
  return `Could not start ${action}. Try again in a moment.`;
}

// ── Cache keys ──────────────────────────────────────────────────────────────

export const billingKeys = {
  all: ["billing"] as const,
  scope: (orgId?: string | null) =>
    [...billingKeys.all, orgId || "personal"] as const,
  plan: (orgId?: string | null) =>
    [...billingKeys.scope(orgId), "plan"] as const,
  wallet: (orgId?: string | null) =>
    [...billingKeys.scope(orgId), "wallet"] as const,
  usage: (orgId?: string | null, range?: UsageRange) =>
    [...billingKeys.scope(orgId), "usage", range || "30d"] as const,
  invoices: (orgId?: string | null) =>
    [...billingKeys.scope(orgId), "invoices"] as const,
  paymentMethods: (orgId?: string | null) =>
    [...billingKeys.scope(orgId), "payment-methods"] as const,
  seats: (orgId: string) => [...billingKeys.scope(orgId), "seats"] as const,
};

/**
 * Resolve the current scope from the org-switcher store. Returns `null`
 * for personal, or the orgId string. Hooks pass this down to the API.
 */
export function useBillingScope(): string | null {
  return useOrgStore((s) => s.activeOrgId) ?? null;
}

function useEnabled(): boolean {
  return !!useAuthStore((s) => s.user);
}

// ── Queries ─────────────────────────────────────────────────────────────────

export function useBillingPlan() {
  const scope = useBillingScope();
  const enabled = useEnabled();
  return useQuery({
    queryKey: billingKeys.plan(scope),
    queryFn: () => billingApi.getPlan(scope),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Wallet balance — the bedrock query that drives the sidebar pill, the
 * chat composer's "send disabled" gate, and the wallet card on
 * `/settings/billing`. Refetches on a short interval so the sidebar
 * pill stays roughly current during long sessions; chat-turn completion
 * also invalidates this key explicitly.
 */
export function useWallet() {
  const scope = useBillingScope();
  const enabled = useEnabled();
  return useQuery({
    queryKey: billingKeys.wallet(scope),
    queryFn: () => billingApi.getWallet(scope),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useUsageBreakdown(range: UsageRange = "30d") {
  const scope = useBillingScope();
  const enabled = useEnabled();
  return useQuery({
    queryKey: billingKeys.usage(scope, range),
    queryFn: () => billingApi.getUsageBreakdown(range, scope),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useInvoices() {
  const scope = useBillingScope();
  const enabled = useEnabled();
  return useQuery({
    queryKey: billingKeys.invoices(scope),
    queryFn: () => billingApi.getInvoices(scope),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function usePaymentMethods() {
  const scope = useBillingScope();
  const enabled = useEnabled();
  return useQuery({
    queryKey: billingKeys.paymentMethods(scope),
    queryFn: () => billingApi.getPaymentMethods(scope),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSeatUsage(orgId: string | null) {
  const enabled = useEnabled() && !!orgId;
  return useQuery({
    queryKey: orgId ? billingKeys.seats(orgId) : ["billing", "seats", "noop"],
    queryFn: () => {
      // Gated by `enabled`; the runtime check is a type-narrowing
      // alternative to `as string`.
      if (!orgId) throw new Error("orgId is required");
      return billingApi.getSeatUsage(orgId);
    },
    enabled,
    staleTime: 60_000,
  });
}

// ── Mutations ───────────────────────────────────────────────────────────────

/**
 * Helper: invalidate every billing query for the active scope after a
 * mutation succeeds. Keeps the sidebar pill, wallet card, invoice list,
 * and plan card in lockstep without each hook caring about the others.
 */
function useInvalidateBilling() {
  const scope = useBillingScope();
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: billingKeys.scope(scope) });
  };
}

export function useStartUpgrade() {
  const scope = useBillingScope();
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: async (args: { planId: "pro" | "team"; seats?: number }) =>
      billingApi.createCheckoutSession({
        ...args,
        organisationId: scope,
      }),
    onSuccess: (res) => {
      // Real Stripe Checkout would redirect away. The mock returns us
      // straight to the billing page with `?upgrade=success`. Mirror the
      // production redirect either way so the rest of the flow is uniform.
      invalidate();
      window.location.href = res.url;
    },
    onError: (err) => {
      toast.error(billingErrorMessage(err, "upgrade"));
    },
  });
}

export function useStartTopup() {
  const scope = useBillingScope();
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: async (args: { packId: TopUpPackId }) =>
      billingApi.createTopupSession({ ...args, organisationId: scope }),
    onSuccess: (res) => {
      invalidate();
      window.location.href = res.url;
    },
    onError: (err) => {
      toast.error(billingErrorMessage(err, "top-up"));
    },
  });
}

export function useConfigureAutoRecharge() {
  const scope = useBillingScope();
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: async (cfg: AutoRechargeConfig) =>
      billingApi.configureAutoRecharge(cfg, scope),
    onSuccess: () => {
      invalidate();
      toast.success("Auto-recharge updated");
    },
    onError: () => {
      toast.error("Could not save auto-recharge settings");
    },
  });
}

export function useOpenStripePortal() {
  const scope = useBillingScope();
  return useMutation({
    mutationFn: async () => billingApi.createPortalLink(scope),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
    onError: () => {
      toast.error("Could not open billing portal");
    },
  });
}
